import React from 'react';
import { Box } from '@mui/material';

/**
 * AppBackdrop
 * ===========
 *
 * Full-bleed ambient gradient + dot-grid that sits behind every main
 * app page (rendered once inside ``.content-body`` in App.js).
 *
 * Visual language is lifted from the marketing site (predict.ai):
 * a soft radial glow anchored at the top, lime in dark mode / teal in
 * light mode, over a faint dot grid that fades out toward the middle
 * of the viewport.
 *
 * Stacking
 * --------
 * ``.content-body`` is given ``isolation: isolate`` (see App.css) so it
 * forms its own stacking context. This backdrop is ``position: fixed``
 * with ``z-index: -1``, which — inside an isolated parent — paints
 * ABOVE the content-body background but BELOW the page content (which
 * is non-positioned, in normal flow). The fixed header (z 100) and
 * sidebar (z 101) live in the root stacking context and still render
 * on top. ``isolation: isolate`` does not create a containing block
 * for fixed elements, so the backdrop stays viewport-anchored.
 *
 * It spans from the sidebar's right edge (``--sidebar-width``) to the
 * right edge of the screen, edge to edge, with no gutter margins — and
 * follows the sidebar when it collapses.
 */
const AppBackdrop = () => (
  <Box
    aria-hidden
    sx={{
      position: 'fixed',
      top: 0,
      bottom: 0,
      left: 'var(--sidebar-width, 180px)',
      right: 0,
      pointerEvents: 'none',
      overflow: 'hidden',
      zIndex: -1,
      transition: 'left 200ms ease',
      // Soft radial gradient floor. Accents tuned per-mode so dark mode
      // glows lime while light mode keeps a calm teal hue.
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        backgroundImage: (t) => t.palette.mode === 'dark'
          ? 'radial-gradient(ellipse 60% 35% at 30% -5%, rgba(192, 251, 110, 0.10), transparent 60%), radial-gradient(ellipse 40% 30% at 90% 0%, rgba(25, 117, 99, 0.16), transparent 60%)'
          : 'radial-gradient(ellipse 60% 35% at 30% -5%, rgba(25, 117, 99, 0.10), transparent 60%), radial-gradient(ellipse 40% 30% at 90% 0%, rgba(192, 251, 110, 0.18), transparent 60%)',
      },
      // Faint dot grid, masked to fade out toward the middle.
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        backgroundImage: (t) => t.palette.mode === 'dark'
          ? 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)'
          : 'radial-gradient(rgba(0,0,0,0.05) 1px, transparent 1px)',
        backgroundSize: '24px 24px',
        maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 70%)',
        WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 70%)',
        opacity: 0.6,
      },
    }}
  />
);

export default AppBackdrop;
